import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CASE_STATUSES, caseStatusFromSession, formatSlot } from "@/lib/caseLog";

const EMPTY = { issue_description: "", steps_taken: "", resolution_notes: "", status: "in_progress", duration_minutes: "", customer_satisfaction: "" };

function StatusPicker({ value, onChange }) {
  return (
    <div className="mt-1 grid grid-cols-3 gap-2" role="radiogroup" data-testid="caselog-status-picker">
      {CASE_STATUSES.map((s) => (
        <button key={s.value} type="button" role="radio" aria-checked={value === s.value} data-testid={`caselog-status-${s.value}`}
          onClick={() => onChange(s.value)}
          className={`h-10 rounded-lg border text-xs font-semibold touch-manipulation transition-colors ${value === s.value ? "bg-brand text-white border-brand ring-2 ring-offset-1 ring-blue-300" : "bg-white text-slate-800 border-slate-200 hover:border-slate-400"}`}>
          {s.label}
        </button>
      ))}
    </div>
  );
}

export default function CaseLogDialog({ session, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!session) { return; }
    const log = session.case_log || {};
    setForm({
      issue_description: log.issue_description || session.issue_description || "",
      steps_taken: log.steps_taken || "",
      resolution_notes: log.resolution_notes || "",
      status: caseStatusFromSession(session),
      duration_minutes: log.duration_minutes ? String(log.duration_minutes) : "",
      customer_satisfaction: log.customer_satisfaction || "",
    });
  }, [session]);

  if (!session) return null;

  const save = async () => {
    const duration = parseInt(form.duration_minutes, 10);
    if (form.issue_description.trim().length < 3) { toast.error("Add the issue description"); return; }
    if (form.resolution_notes.trim().length < 3) { toast.error("Add resolution notes"); return; }
    if (!duration || duration < 1) { toast.error("Enter the duration in minutes"); return; }
    setLoading(true);
    try {
      await api.patch(`/technician/sessions/${session.id}/case-log`, { ...form, duration_minutes: duration });
      toast.success("Case log saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const { date, slot } = formatSlot(session.scheduled_at);
  return (
    <Dialog open={!!session} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="caselog-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Case log</DialogTitle>
        </DialogHeader>
        <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-900">
          <div className="font-semibold">{session.user_name} · {session.category}</div>
          <div className="text-slate-500 mt-1">{date} · {slot}</div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="font-semibold">Issue description *</Label>
            <Textarea data-testid="caselog-issue-input" rows={3} value={form.issue_description} onChange={set("issue_description")} className="mt-1 border border-slate-200" />
          </div>
          <div>
            <Label className="font-semibold">Steps taken</Label>
            <Textarea data-testid="caselog-steps-input" rows={3} value={form.steps_taken} onChange={set("steps_taken")} className="mt-1 border border-slate-200" />
          </div>
          <div>
            <Label className="font-semibold">Resolution notes *</Label>
            <Textarea data-testid="caselog-resolution-input" rows={3} value={form.resolution_notes} onChange={set("resolution_notes")} className="mt-1 border border-slate-200" />
          </div>
          <div>
            <Label className="font-semibold">Status *</Label>
            <StatusPicker value={form.status} onChange={(v) => setForm({ ...form, status: v })} />
          </div>
          <div>
            <Label className="font-semibold">Duration taken (minutes) *</Label>
            <Input data-testid="caselog-duration-input" type="number" min={1} inputMode="numeric" value={form.duration_minutes} onChange={set("duration_minutes")} className="mt-1 border border-slate-200 h-11" />
          </div>
          <div>
            <Label className="font-semibold">Customer satisfaction noted</Label>
            <Input data-testid="caselog-satisfaction-input" placeholder="e.g. Happy with the fix" value={form.customer_satisfaction} onChange={set("customer_satisfaction")} className="mt-1 border border-slate-200 h-11" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={loading} data-testid="caselog-save-btn" className="bg-brand hover:bg-brand-dark text-white font-bold shadow-btn">
            {loading ? "Saving…" : "Save case log"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
